/**
 * @file tool-manager.ts
 * @description Manages tool selection, lifecycle cleanup, and undo/redo operations.
 */

import { g, Tool } from '@hcie/core';
import { EventBus } from '@hcie/shared';
import { deselect } from '@hcie/tools';
import { updateCanvasCursor } from './cursor-manager';

/**
 * @function selectTool
 * @description Sets the current active tool and handles the necessary cleanup for previous selection states.
 * @param {Tool} nextTool - The tool to be activated.
 * @returns {void}
 */
export function selectTool(nextTool: Tool): void {
    if (!nextTool) {
        return;
    }
    
    // [CRITICAL_LOGIC_START]
    // TOOL CLEANUP: When switching away from or between selection tools,
    // we must clear active handles to prevent them from persisting into other modes.
    const selectionToolIds: readonly string[] = [
        'btn-rect-select', 'btn-ellipse-select', 'btn-circle-select',
        'btn-lasso', 'btn-poly-select', 'btn-single-row-select', 'btn-single-column-select'
    ];

    const currentToolId: string = g.current_tool?.id || '';
    const isPreviousToolSelection: boolean = selectionToolIds.includes(currentToolId);
    const isToolChanging: boolean = currentToolId !== nextTool.id;

    if (isPreviousToolSelection && isToolChanging) {
        // Clear ghost selection handle state
        (g as any)._selectionEditBounds = null;
        (g as any)._draggingCornerIndex = -1;
        (g as any)._draggingPointIndex = -1;
        
        // Committing ghost selection if active before switching.
        if (g.isSelectionActive && (g as any).settings?.selection?.ghostMode) {
            if (typeof (window as any).commitDrawing === 'function') {
                (window as any).commitDrawing();
            }
        }
    }

    g.current_tool = nextTool;
    updateCanvasCursor();

    // UI SYNC: Update 'active' class on toolbox buttons (Bug 49 Fix)
    if (typeof document !== 'undefined') {
        const buttons = document.querySelectorAll('.div-toolbox .button');
        buttons.forEach(btn => btn.classList.remove('active'));
        const activeBtn = document.getElementById(nextTool.id) || document.getElementById(`btn-${nextTool.id.toLowerCase()}`);
        if (activeBtn) {
            activeBtn.classList.add('active');
        }
    }
    
    // Notify components via EventBus
    EventBus.emit('TOOL_CHANGED', { toolId: nextTool.id });
    
    // Sync UI components that still rely on global functions
    if (typeof (window as any).updateOptionsBar === 'function') {
        (window as any).updateOptionsBar();
    }
    // [CRITICAL_LOGIC_END]
}

/**
 * @function undoAction
 * @description Triggers the global undo operation and notifies the UI.
 * @returns {void}
 */
export function undoAction(): void {
    if (window.historyManager) {
        window.historyManager.undo();
        EventBus.emit('RENDER_LAYERS');
    }
}

/**
 * @function redoAction
 * @description Triggers the global redo operation and notifies the UI.
 * @returns {void}
 */
export function redoAction(): void {
    if (window.historyManager) {
        window.historyManager.redo();
        EventBus.emit('RENDER_LAYERS');
    }
}

/**
 * @function clearSelectionContent
 * @description Deselects the current selection area.
 * @returns {void}
 */
export function clearSelectionContent(): void {
    deselect();
}
